import { Button, Card, Form, Input, Message, Select, Space, Table } from "@arco-design/web-react";
import { useCallback, useEffect, useState } from "react";
import { WorkOrderAPI } from "@/apis/manufacturing";
import { IconPlus } from "@arco-design/web-react/icon";
import { Link } from "react-router-dom";
import { WorkOrderStatusDef, WorkOrderStatus } from "@/enums/manufacturing";
import { EnumLabel } from "@/components/Enum";
import { Number } from "@/components/Numeric";
import { formatDate } from "@/utils";
import { WorkOrderAdd } from "./wo-add";
import { WorkOrderInfo } from "./wo-info";
import { WorkOrderEdit } from "./wo-edit";

const FormItem = Form.Item;
const Option = Select.Option;

const OPEN_STATUSES = [
    WorkOrderStatus.Created,
    WorkOrderStatus.Issued,
    WorkOrderStatus.Picked,
    WorkOrderStatus.PartialPicked,
    WorkOrderStatus.PartialPickedFulfilled,
    WorkOrderStatus.PartialFulfilled,
];

const toStatuses = (statusValue) => {
    if (!statusValue || statusValue === 0) return undefined;
    if (statusValue === "open") return OPEN_STATUSES;
    return [statusValue];
};

export const WorkOrder = () => {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [queryParams, setQueryParams] = useState({ statuses: OPEN_STATUSES });

    const pagination = { total, current: page, pageSize: 20 };

    const getWorkOrders = useCallback(async () => {
        setLoading(true);
        try {
            const values = await WorkOrderAPI.query({ page, ...queryParams });
            setData(values.records);
            setTotal(values.total);
        } catch (error) {
            Message.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [page, queryParams]);

    useEffect(() => {
        getWorkOrders();
    }, [getWorkOrders]);

    const onFieldChange = (field, value) => {
        const formValues = form.getFieldsValue();
        const params = {
            finishedItemName: formValues.finishedItemName,
            statuses: toStatuses(formValues.status),
        };
        if (field !== "status") {
            params[field] = value;
        }
        setQueryParams(params);
        setPage(1);
    };

    const columns = [
        {
            title: "WO#",
            dataIndex: "workOrderNo",
            render: (workOrderNo, record) => (
                <Link to={`/workspace/manufacturing/work-order/${record.id}`}>{workOrderNo}</Link>
            ),
        },
        {
            title: "Created Date",
            dataIndex: "createTime",
            render: (createTime) => createTime ? formatDate(createTime) : "",
        },
        {
            title: "Production Date",
            dataIndex: "productionDate",
            render: (productionDate) => productionDate || "",
        },
        {
            title: "BOM Name",
            dataIndex: "bomName",
            render: (bomName, record) => (
                record.bomId ? <Link to={`/workspace/manufacturing/bom/${record.bomId}`}>{bomName}</Link> : bomName
            ),
        },
        {
            title: "WO Qty",
            dataIndex: "quantity",
            render: (quantity) => <Number value={quantity} />,
        },
        {
            title: "Fulfilled Qty",
            dataIndex: "fulfilledQty",
            render: (fulfilledQty) => <Number value={fulfilledQty} />,
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (status) => <EnumLabel enumObj={WorkOrderStatusDef} value={status} />,
        },
    ];

    return (
        <Card title="Work Order" extra={
            <Space size="large">
                <Form form={form} layout="inline" initialValues={{ status: "open" }}>
                    <FormItem field="finishedItemName" style={{ marginBottom: 0, marginRight: 8 }}>
                        <Input placeholder="Finished Item Name" allowClear onChange={(val) => onFieldChange("finishedItemName", val)} style={{ width: 180 }} />
                    </FormItem>
                    <FormItem field="status" style={{ marginBottom: 0, marginRight: 8 }}>
                        <Select placeholder="Status" allowClear onChange={(val) => onFieldChange("status", val)} style={{ width: 160 }}>
                            <Option value={0}>All</Option>
                            <Option value="open">Open</Option>
                            {Object.entries(WorkOrderStatusDef).map(([, item]) => (
                                <Option key={item.value} value={item.value}>{item.label}</Option>
                            ))}
                        </Select>
                    </FormItem>
                </Form>
                <Link to="/workspace/manufacturing/work-order/add">
                    <Button type="primary" icon={<IconPlus />}>Add Work Order</Button>
                </Link>
            </Space>
        }>
            <Table
                rowKey="id"
                columns={columns}
                data={data}
                loading={loading}
                pagination={pagination}
                onChange={(pagination) => setPage(pagination.current)}
            />
        </Card>
    );
};

WorkOrder.Add = WorkOrderAdd;
WorkOrder.Info = WorkOrderInfo;
WorkOrder.Edit = WorkOrderEdit;
